import { supabase } from '../integrations/supabase/client.ts';

interface OfferPageProduct {
  product_id: string;
  sort_order: number;
}

interface OfferPage {
  id: string;
  heading?: string;
  title?: string;
  subheading?: string;
  description?: string;
  slug: string;
  status?: string;
  created_at: string;
  user_id: string;
  is_test?: boolean;
  isTestData?: boolean;
  products?: OfferPageProduct[];
}

interface PaginatedOfferPagesResponse {
  offerPages: OfferPage[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
}

interface OfferPagesFilters {
  status?: string;
  searchQuery?: string;
}

export const fetchPaginatedOfferPages = async (
  page: number = 1,
  limit: number = 10,
  filters: OfferPagesFilters = {}
): Promise<PaginatedOfferPagesResponse> => {
  try {
 
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.error('Error fetching user or user not authenticated:', userError);
      return {
        offerPages: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: page,
        hasMore: false
      };
    }

    const userId = userData.user.id;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('offer_pages')
      .select(`
        *,
        products:offer_page_products!offer_page_products_offer_page_id_fkey(
          product_id,
          sort_order
        )
      `, { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const searchTerm = filters.searchQuery.toLowerCase().trim();
      query = query.or(`heading.ilike.%${searchTerm}%,subheading.ilike.%${searchTerm}%,id.ilike.%${searchTerm}%`);
    }

    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching paginated offer pages:', error);
      throw error;
    }

    const offerPages: OfferPage[] = (data || []).map(page => ({
      ...page,
      title: page.heading || 'Unnamed Offer',
      description: page.subheading || '',
      status: page.status || 'active',
      isTestData: page.is_test || false
    }));

    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const hasMore = page < totalPages;

    return {
      offerPages,
      totalCount,
      totalPages,
      currentPage: page,
      hasMore
    };

  } catch (error) {
    console.error('Error in fetchPaginatedOfferPages:', error);
    return {
      offerPages: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: page,
      hasMore: false
    };
  }
};

export const updateOfferPageStatus = async (id: string, status: 'active' | 'archived'): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('offer_pages')
      .update({ status })
      .eq('id', id);

    if (error) {
      console.error('Error updating offer page status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateOfferPageStatus:', error);
    return false;
  }
};

export type { OfferPagesFilters, PaginatedOfferPagesResponse, OfferPage, OfferPageProduct };